import {
  AfterViewInit,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostBinding,
  Input,
  OnDestroy,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, UntypedFormControl, NG_VALUE_ACCESSOR, Validators } from '@angular/forms';

import { fromEvent, Subscription } from 'rxjs';
import { debounceTime, map, tap, withLatestFrom } from 'rxjs/operators';

import { collapseExpand } from '../../animations/animations';

import {
  AutocompleteContainerTemplateDirective,
  AutocompleteFooterTemplateDirective,
  AutocompleteHeaderTemplateDirective,
} from './autocomplete.directive';
import {
  AutocompleteRequest,
  AutocompleteResponse,
  ContainerTemplateModel,
  FooterTemplateModel,
  HeaderTemplateModel,
  SearchMethodEvent,
} from '../../models';

interface ClickOption {
  heading: string;
  options: { [key: string]: string | number }[];
}

@Component({
  selector: 'rx-autocomplete',
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true,
    },
  ],
  animations: [collapseExpand],
})
export class AutocompleteComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  public query: UntypedFormControl = new UntypedFormControl('', Validators.minLength(1));
  public focused = false;
  public rowLocation: number;
  public isInputHover = false;
  private _innerValue: { [key: string]: string | number } | string = {};
  private subscription = new Subscription();

  /** Placeholder text */
  @Input() public placeholder?: string;

  /** Label text */
  @Input() public label?: string;

  @Input() public leftIcon?: string;

  @Input() public cleanFieldIcon = false;

  /** Secondary label text */
  @Input() public secondaryLabel?: string;

  /** Hint text (appears right next to the label) */
  @Input() public hint?: string;

  /** Error text (appears bellow the input in red) */
  @Input() public error?: string;

  /** Options object */
  @Input() public options: AutocompleteResponse[] = [];

  /** Options object for the dropdown which is shown on clicking/focusing the input */
  @Input() public clickOptions: ClickOption[] = [];

  /** Dropdown menu options header */
  @Input() public clickOptionsHeader: string;

  /** Debounce time for search query */
  @Input() public debounce?: number;

  @Input() public bindValue: string;

  @Input() public bindLabel = '';

  @Input() public bindAppend?: string;

  /** Show loading modal */
  @Input() public showLoading: boolean;

  /** Hover state */
  @Input() public isHover = false;

  @Input() public selectFirsItemIfEmptyOnBlur = false;

  /** Search query event */
  @Output() searchMethod: EventEmitter<SearchMethodEvent> = new EventEmitter();

  /** Dropdown item selected */
  // eslint-disable-next-line @angular-eslint/no-output-on-prefix
  @Output() onSelect: EventEmitter<AutocompleteRequest> = new EventEmitter();

  /** Compact / smaller version */
  @HostBinding('class.compact') @Input() compact: boolean;

  @ContentChild(AutocompleteHeaderTemplateDirective, {
    read: TemplateRef,
  })
  headerTemplate: TemplateRef<HeaderTemplateModel>;
  @ContentChild(AutocompleteContainerTemplateDirective, {
    read: TemplateRef,
  })
  containerTemplate: TemplateRef<ContainerTemplateModel>;

  @ContentChild(AutocompleteFooterTemplateDirective, {
    read: TemplateRef,
  })
  footerTemplate: TemplateRef<FooterTemplateModel>;

  @ContentChild(TemplateRef, { read: TemplateRef })
  templateRef: TemplateRef<Element>;
  @ViewChild('autocompleteInput', { read: ElementRef })
  autocompleteInput: ElementRef;

  public ngAfterViewInit() {
    // initialize the code
    this.rowLocation = 0;
    this._innerValue = { [this.bindValue]: '', [this.bindLabel]: '' };

    this.subscription.add(
      fromEvent(this.autocompleteInput.nativeElement, 'keyup')
        .pipe(
          tap(() => (this.focused = true)),
          debounceTime(this.debounce || 100),
          withLatestFrom(this.query.valueChanges),
          map(([_, query]: [KeyboardEvent, string]) => {
            if (this._innerValue[this.bindLabel] && query !== this._innerValue[this.bindLabel]) {
              this._innerValue = { [this.bindValue]: '', [this.bindLabel]: '' };
              this.onChange(this._innerValue);
              this.rowLocation = 0;
            }
            return query;
          }),
          tap((query) => this.search(query)),
        )
        .subscribe(),
    );
  }

  search(query: string) {
    // allow empty string but not undefined or null
    if (query === undefined || query === null) {
      return;
    }

    this.searchMethod.emit({ query });
  }

  // This is used to open the list box when click on textbox
  public onClick() {
    this.focused = true;
  }

  // This is used to open the list box
  public onFocus() {
    this.focused = true;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public selectItem(item: any) {
    this.query.setValue(item[this.bindLabel]);
    this._innerValue = item;
    this.onChange(this._innerValue);
    this.focused = false;
    this.onSelect.emit(item);
  }

  public ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  public onChange = (value) => {
    /**/
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public writeValue(obj: any) {
    if (obj && obj[this.bindValue]) {
      this._innerValue = obj;
      this.query.setValue(obj[this.bindLabel]);
    } else if (typeof obj === 'string' && this._innerValue !== obj) {
      this._innerValue = obj;
      this.query.setValue(obj);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched = (_fn: () => void) => {
    /**/
  };

  public setDisabledState(isDisabled: boolean): void {
    if (isDisabled) {
      this.query.disable();
    } else {
      this.query.enable();
    }
  }

  // This function is used to close the option list when click outside the textbox and list
  handleClickOutside() {
    if (this.selectFirsItemIfEmptyOnBlur && this.focused && this.options && this.options.length) {
      this.selectItem(this.options[0]);
    }
    this.focused = false;
  }

  onMouseHover() {
    this.isInputHover = this.isHover;
  }
  onMouseLeave() {
    this.isInputHover = false;
  }
}
